import {
  showLoader,
  hideLoader,
  getServicesList,
  WorkflowProcessType,
  WorkflowInstanceType,
  LayoutResponseType,
  WORKFLOW_UI_IDENTIFIER,
} from '@app-store'
import { getEntityDataByIdEmbedded } from '../../apis/entityData.api'
import EntityLayoutCasePane from '../../components/workflow/EntityLayoutTask'
import { useAppDispatch } from '../../utils/agent.store'
import { useContext, useEffect, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import { isEmpty } from 'lodash'
import { SvgPencilEditIcon } from '@ui-kit'
import FormRenderer from '../../components/FormRenderer'
import { EntityLayout, LayoutData, Page } from '../../types/layout.types'
import { CallInteractionContext } from './CallInteactionContext'
import { TabIndexMap } from '../../types/common'
import { generateTabIndexMap } from '../../utils/tab-indexing'
import { ShortcutKeys } from '../../constants/form.constants'
import { useNavigate } from 'react-router-dom'
import HybridLayout from '../../components/hybird-layout/HybridLayout'
import { INTERACTION_STATUS } from '../../utils/constants'
import { fetchCaseData } from '../../utils/helperFunctions'

const EntityLayoutWrapper = ({
  definition,
  instance,
  updateLabel,
  caseId,
  workflowIndex = 0,
  caseDetailsValue,
  interactionStatus,
}: {
  definition?: WorkflowProcessType
  instance?: WorkflowInstanceType
  updateLabel?: (workflowIndex?: number) => void
  caseId?: string
  workflowIndex?: number
  caseDetailsValue?: { case_key: string; case_status_id: number }
  interactionStatus?: FieldValues
}) => {
  const {
    isEditMode,
    handleIsEditMode,
    tabIndexOffset,
    layoutInfo,
    setLayoutInfo,
  } = useContext(CallInteractionContext)
  const [caseDetails, setCaseDetails] = useState<FieldValues>({})
  const [showEditButton, setShowEditButton] = useState(
    isEditMode?.[workflowIndex] === false
  )
  const [serviceName, setServiceName] = useState('')
  const [workflowDefinition] = useState(definition)
  const [workflowInstance] = useState(instance)
  const [renderKey, setRenderKey] = useState<boolean>(false)
  const [tabIndexMapForFormRenderer, setTabIndexMapForFormRenderer] =
    useState<TabIndexMap>({})
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const fetchServiceName = async () => {
    try {
      dispatch(showLoader())
      const response = await getServicesList()
      const service_id =
        workflowDefinition?.global_variables?.inherited_variables?.service_id
      if (response?.data && service_id) {
        setServiceName(
          response?.data?.find(
            (item: (typeof response.data)[0]) => item?.id === service_id
          )?.service_name
        )
      }
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchCaseDetails = async () => {
    dispatch(showLoader())
    try {
      let response: {
        data?: {
          case_status_id: number
          case_key: string
        }
      } = {}
      if (caseDetailsValue) {
        response.data = caseDetailsValue
      } else {
        response = await getEntityDataByIdEmbedded(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(workflowDefinition?.global_variables?.entity_id),
          [
            workflowDefinition?.global_variables?.config?.case_status ?? '',
            workflowDefinition?.global_variables?.config?.case_id ?? '',
          ],
          instance?.case_id ?? ''
        )
      }

      if (response?.data) {
        const res = await getEntityDataByIdEmbedded(
          workflowDefinition?.global_variables?.associated_variables
            ?.case_status?.parent_entity ?? '',
          Number(
            workflowDefinition?.global_variables?.associated_variables
              ?.case_status?.entity_id
          ),
          ['name'],
          response?.data?.case_status_id
        )
        if (res?.data) {
          setCaseDetails({
            caseId: response?.data?.case_key ?? '',
            caseStatus: res?.data?.name ?? '',
          })
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const onButtonClick = () => {
    if (workflowIndex !== undefined) {
      const newIsEditMode = { ...isEditMode }
      newIsEditMode[workflowIndex] = false
      handleIsEditMode?.(newIsEditMode)
    }
    updateLabel?.(workflowIndex)
    setShowEditButton(true)
  }

  const handleEditButtonClick = () => {
    if (workflowIndex !== undefined) {
      const newIsEditMode = { ...isEditMode }
      newIsEditMode[workflowIndex] = true
      handleIsEditMode?.(newIsEditMode)
    }
    setShowEditButton(false)
  }

  useEffect(() => {
    const handler = async () => {
      try {
        const response = await getEntityDataByIdEmbedded(
          workflowDefinition?.global_variables?.entity_name ?? '',
          Number(workflowDefinition?.global_variables?.entity_id),
          [
            workflowDefinition?.global_variables?.config?.case_status ?? '',
            workflowDefinition?.global_variables?.config?.case_id ?? '',
          ],
          instance?.case_id ?? ''
        )
        if (response?.data) {
          const res = await getEntityDataByIdEmbedded(
            workflowDefinition?.global_variables?.associated_variables
              ?.case_status?.parent_entity ?? '',
            Number(
              workflowDefinition?.global_variables?.associated_variables
                ?.case_status?.entity_id
            ),
            ['name'],
            response?.data?.case_status_id
          )
          if (res?.data) {
            setCaseDetails({
              caseId: response?.data?.case_key ?? '',
              caseStatus: res?.data?.name ?? '',
            })
          }
        }
      } catch (err) {
        console.error(err)
      }
    }
    window.addEventListener('refetchthestatus', handler)

    return () => {
      window.removeEventListener('refetchthestatus', handler)
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(workflowInstance)) {
      fetchCaseDetails()
    }
    fetchServiceName()
  }, [workflowInstance, workflowDefinition])

  useEffect(() => {
    if (layoutInfo?.[workflowIndex]?.layout?.layout_data?.pages) {
      setTabIndexMapForFormRenderer(
        generateTabIndexMap(
          layoutInfo?.[workflowIndex]?.layout?.layout_data
            ?.pages as unknown as Page[],
          tabIndexOffset
        )
      )
    } else {
      setTabIndexMapForFormRenderer({})
    }
  }, [layoutInfo?.[workflowIndex]?.layout?.layout_data, tabIndexOffset])

  useEffect(() => {
    if (caseId) {
      fetchCaseData(caseId || '', dispatch)
      const interactionUrl = window.location.pathname.split('/case')[0]
      navigate(`${interactionUrl}/case/${caseId}`, { replace: true })
    }
  }, [])

  return (
    <div className="relative">
      <div className="font-primary font-semibold text-base pb-4 mt-4">
        {serviceName}
      </div>
      <div
        className={`flex justify-between items-center mb-3 ${
          showEditButton ? '' : 'absolute'
        }`}
      >
        <div className="flex flex-row gap-3 items-center mt-3">
          <span className="font-primary font-semibold text-xs">
            Case ID - {caseDetails?.caseId}
          </span>
          <span>|</span>
          <span className="font-primary font-normal text-xs">
            {caseDetails?.caseStatus}
          </span>
        </div>
        {showEditButton ? (
          <div
            onClick={handleEditButtonClick}
            className={`${
              interactionStatus?.category_status?.label ===
              INTERACTION_STATUS.CANCELLED
                ? 'pointer-events-none opacity-50'
                : 'cursor-pointer'
            }`}
            tabIndex={tabIndexOffset ?? 0}
            onKeyDown={(e) => {
              if (
                e?.key === ShortcutKeys.Enter ||
                e?.key === ShortcutKeys.Space
              ) {
                e?.preventDefault()
                handleEditButtonClick()
              }
            }}
          >
            <SvgPencilEditIcon height={'18'} width={'18'} />
          </div>
        ) : null}
      </div>

      {showEditButton &&
      !(
        isEmpty(layoutInfo?.[workflowIndex]?.layout) &&
        isEmpty(layoutInfo?.[workflowIndex]?.recordData)
      ) ? (
        workflowInstance?.tasks?.[0]?.identifier?.ui_identifier ===
        WORKFLOW_UI_IDENTIFIER.CUSTOM_LAYOUT_SCREEN ? (
          <HybridLayout
            layoutData={
              layoutInfo?.[workflowIndex]?.layout as LayoutResponseType
            }
            isWorkflow={true}
            recordId={
              layoutInfo?.[workflowIndex]?.recordData?.id?.toString() ?? ''
            }
            formHeaders={null}
            openAllAccordions={true}
            hideHeaders={true}
            mode="view"
          />
        ) : (
          <FormRenderer
            mode="view"
            layout={
              layoutInfo?.[workflowIndex]?.layout?.layout_data as LayoutData
            }
            fullLayout={layoutInfo?.[workflowIndex]?.layout as EntityLayout}
            heading={null}
            handleFormSubmit={() => console.log('')}
            data={layoutInfo?.[workflowIndex]?.recordData}
            key={renderKey.toString()}
            tabIndexMap={tabIndexMapForFormRenderer}
          />
        )
      ) : null}

      {!showEditButton && (
        <EntityLayoutCasePane
          definition={definition}
          instance={instance}
          fromInteraction={true}
          onButtonClick={onButtonClick}
          onDataFetch={(
            layout: LayoutResponseType | null,
            recordData: FieldValues | null
          ) => {
            if (layout && setLayoutInfo) {
              setLayoutInfo((prev) => {
                prev[workflowIndex].layout = layout
                return prev
              })
            }
            if (recordData && setLayoutInfo) {
              setLayoutInfo((prev) => {
                prev[workflowIndex].recordData = recordData
                return prev
              })
              setRenderKey((prev) => !prev)
            }
          }}
          tabIndexOffset={tabIndexOffset}
        />
      )}
    </div>
  )
}

export default EntityLayoutWrapper
